import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Leaf, Tree, Car, Airplane, Lightning, Phone, Hamburger, 
  Trophy, Medal, Crown, TrendUp, Users, Fire, Planet, ChartLine
} from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE_COLORS = {
  'Carbon Champion': 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white',
  'Climate Warrior': 'bg-gradient-to-r from-green-500 to-emerald-600 text-white',
  'Green Leader': 'bg-gradient-to-r from-teal-400 to-cyan-500 text-white',
  'Eco Starter': 'bg-gradient-to-r from-blue-400 to-indigo-500 text-white',
  'Beginning': 'bg-gray-200 text-gray-700'
};

const BADGE_ICONS = {
  'Carbon Champion': Crown,
  'Climate Warrior': Fire,
  'Green Leader': Medal,
  'Eco Starter': Leaf,
  'Beginning': Leaf
};

const CarbonDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userImpact, setUserImpact] = useState(null);
  const [loading, setLoading] = useState(true);

  const irisId = localStorage.getItem('ginsgood_iris_id');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/carbon/stats`),
        axios.get(`${API}/carbon/leaderboard?limit=10`)
      ]);

      setStats(statsRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard);

      // Fetch user impact if authenticated
      if (irisId) {
        try {
          const userRes = await axios.get(`${API}/carbon/user/${irisId}/impact`);
          setUserImpact(userRes.data);
        } catch (err) {
          console.log('User impact not available');
        }
      }
    } catch (error) {
      console.error('Error fetching carbon data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <div className="animate-pulse">Loading carbon impact data...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const platformStats = stats?.platform_stats || {};
  const equivalents = stats?.environmental_equivalents || {};

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 text-green-600 border-green-300">
              <Planet weight="duotone" className="w-4 h-4 mr-2" />
              Carbon Impact Dashboard
            </Badge>
            <h1 className="text-3xl md:text-4xl font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Collective Climate Action
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Track the environmental impact of the GinsGood community. Every carbon credit retired makes a difference.
            </p>
          </div>

          {/* Platform Stats Hero */}
          <Card className="p-8 mb-8 bg-gradient-to-br from-green-600 to-emerald-700 text-white" data-testid="platform-stats-card">
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-sm opacity-80 mb-1">Total Carbon Offset</p>
                <p className="text-4xl font-bold">{platformStats.total_carbon_retired_kg?.toLocaleString() || 0}</p>
                <p className="text-sm opacity-80">kg CO2</p>
              </div>
              <div>
                <p className="text-sm opacity-80 mb-1">Retirements</p>
                <p className="text-4xl font-bold">{platformStats.total_retirements?.toLocaleString() || 0}</p>
                <p className="text-sm opacity-80">carbon credits burned</p>
              </div>
              <div>
                <p className="text-sm opacity-80 mb-1">Active Offsetters</p>
                <p className="text-4xl font-bold">{platformStats.unique_offsetters?.toLocaleString() || 0}</p>
                <p className="text-sm opacity-80">participants</p>
              </div>
              <div>
                <p className="text-sm opacity-80 mb-1">GCC Minted</p>
                <p className="text-4xl font-bold">{platformStats.total_gcc_minted?.toLocaleString() || 0}</p>
                <p className="text-sm opacity-80">carbon credits issued</p>
              </div>
            </div>
          </Card>

          {/* Environmental Equivalents Grid */}
          <h2 className="text-xl font-medium mb-4 flex items-center gap-2">
            <Tree weight="duotone" className="w-5 h-5 text-green-600" />
            Environmental Impact Equivalents
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="equivalents-grid">
            <Card className="p-4 text-center hover:shadow-lg transition-shadow">
              <Tree weight="duotone" className="w-8 h-8 mx-auto mb-2 text-green-600" />
              <p className="text-2xl font-bold text-green-700">{equivalents.trees_planted_equivalent || 0}</p>
              <p className="text-xs text-muted-foreground">Trees Planted (1 year)</p>
            </Card>
            <Card className="p-4 text-center hover:shadow-lg transition-shadow">
              <Car weight="duotone" className="w-8 h-8 mx-auto mb-2 text-blue-600" />
              <p className="text-2xl font-bold text-blue-700">{equivalents.km_not_driven?.toLocaleString() || 0}</p>
              <p className="text-xs text-muted-foreground">km Not Driven</p>
            </Card>
            <Card className="p-4 text-center hover:shadow-lg transition-shadow">
              <Airplane weight="duotone" className="w-8 h-8 mx-auto mb-2 text-purple-600" />
              <p className="text-2xl font-bold text-purple-700">{equivalents.flights_avoided_km?.toLocaleString() || 0}</p>
              <p className="text-xs text-muted-foreground">Flight km Avoided</p>
            </Card>
            <Card className="p-4 text-center hover:shadow-lg transition-shadow">
              <Lightning weight="duotone" className="w-8 h-8 mx-auto mb-2 text-yellow-600" />
              <p className="text-2xl font-bold text-yellow-700">{equivalents.led_bulb_hours?.toLocaleString() || 0}</p>
              <p className="text-xs text-muted-foreground">LED Bulb Hours</p>
            </Card>
            <Card className="p-4 text-center hover:shadow-lg transition-shadow">
              <Phone weight="duotone" className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <p className="text-2xl font-bold text-gray-700">{equivalents.smartphones_charged?.toLocaleString() || 0}</p>
              <p className="text-xs text-muted-foreground">Smartphones Charged</p>
            </Card>
            <Card className="p-4 text-center hover:shadow-lg transition-shadow">
              <Hamburger weight="duotone" className="w-8 h-8 mx-auto mb-2 text-red-600" />
              <p className="text-2xl font-bold text-red-700">{equivalents.beef_meals_avoided || 0}</p>
              <p className="text-xs text-muted-foreground">Beef Meals Avoided</p>
            </Card>
            <Card className="p-4 text-center hover:shadow-lg transition-shadow">
              <ChartLine weight="duotone" className="w-8 h-8 mx-auto mb-2 text-indigo-600" />
              <p className="text-2xl font-bold text-indigo-700">{equivalents.streaming_hours_offset?.toLocaleString() || 0}</p>
              <p className="text-xs text-muted-foreground">Streaming Hours Offset</p>
            </Card>
            <Card className="p-4 text-center bg-gradient-to-br from-green-50 to-emerald-100 hover:shadow-lg transition-shadow">
              <Planet weight="duotone" className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
              <p className="text-2xl font-bold text-emerald-700">{equivalents.carbon_offset_tons || 0}</p>
              <p className="text-xs text-muted-foreground">Metric Tons CO2</p>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Leaderboard */}
            <div>
              <h2 className="text-xl font-medium mb-4 flex items-center gap-2">
                <Trophy weight="duotone" className="w-5 h-5 text-yellow-500" />
                Top Carbon Offsetters
              </h2>
              <Card className="p-6" data-testid="leaderboard-card">
                {leaderboard.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No carbon retirements yet. Be the first!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {leaderboard.map((entry, idx) => {
                      const BadgeIcon = BADGE_ICONS[entry.badge] || Leaf;
                      return (
                        <div 
                          key={entry.iris_id}
                          className={`flex items-center justify-between p-4 rounded-lg ${
                            entry.iris_id === irisId ? 'bg-green-50 border border-green-200' : 'bg-muted/50'
                          }`}
                          data-testid={`leaderboard-${idx}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                              idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                              idx === 1 ? 'bg-gray-100 text-gray-700' :
                              idx === 2 ? 'bg-orange-100 text-orange-700' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-medium">{entry.name}</p>
                              <div className="flex items-center gap-2">
                                <Badge className={`text-xs ${BADGE_COLORS[entry.badge]}`}>
                                  <BadgeIcon className="w-3 h-3 mr-1" />
                                  {entry.badge}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">{entry.total_carbon_offset_kg} kg</p>
                            <p className="text-xs text-muted-foreground">{entry.retirement_count} retirements</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>

            {/* User Impact (if authenticated) */}
            <div>
              <h2 className="text-xl font-medium mb-4 flex items-center gap-2">
                <Users weight="duotone" className="w-5 h-5 text-blue-500" />
                Your Impact
              </h2>
              {!irisId ? (
                <Card className="p-6 text-center" data-testid="user-impact-card">
                  <Leaf className="w-12 h-12 mx-auto mb-4 text-green-500 opacity-50" />
                  <p className="text-muted-foreground mb-4">Register to track your personal carbon impact</p>
                  <Button onClick={() => navigate('/iris-auth')}>Get Started</Button>
                </Card>
              ) : userImpact ? (
                <Card className="p-6" data-testid="user-impact-card">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="text-sm text-muted-foreground">Your Total Offset</p>
                      <p className="text-3xl font-bold text-green-600">
                        {userImpact.carbon_impact?.total_carbon_retired_kg || 0} kg
                      </p>
                    </div>
                    <Badge className={BADGE_COLORS[userImpact.badge]}>
                      {userImpact.badge}
                    </Badge>
                  </div>

                  {userImpact.rank?.position && (
                    <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Your Rank</span>
                        <span className="font-bold">#{userImpact.rank.position} of {userImpact.rank.total_participants}</span>
                      </div>
                      <Progress 
                        value={userImpact.rank.percentile || 0} 
                        className="h-2 mt-2" 
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Top {(100 - (userImpact.rank.percentile || 0)).toFixed(0)}% of offsetters
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Trees Equivalent</p>
                      <p className="font-bold text-green-700">
                        {userImpact.environmental_equivalents?.trees_planted_equivalent || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">km Not Driven</p>
                      <p className="font-bold text-blue-700">
                        {userImpact.environmental_equivalents?.km_not_driven?.toLocaleString() || 0}
                      </p>
                    </div>
                  </div>

                  <Button onClick={() => navigate('/wallet')} className="w-full">
                    Go to Wallet to Retire More
                  </Button>
                </Card>
              ) : (
                <Card className="p-6 text-center" data-testid="user-impact-card">
                  <Leaf className="w-12 h-12 mx-auto mb-4 text-green-500 opacity-50" />
                  <p className="text-muted-foreground mb-4">You haven't retired any carbon credits yet</p>
                  <Button onClick={() => navigate('/wallet')}>Start Offsetting</Button>
                </Card>
              )}

              {/* Purpose Breakdown */}
              {stats?.purpose_breakdown && stats.purpose_breakdown.length > 0 && (
                <Card className="p-6 mt-4" data-testid="purpose-breakdown-card">
                  <h3 className="font-medium mb-4 flex items-center gap-2">
                    <TrendUp className="w-4 h-4" />
                    Offset Purposes
                  </h3>
                  <div className="space-y-2">
                    {stats.purpose_breakdown.map((p, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="capitalize text-sm">{p.purpose.replace(/_/g, ' ')}</span>
                        <span className="font-medium">{p.carbon_kg} kg ({p.count})</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>

          {/* Call to Action */}
          <Card className="mt-10 p-8 text-center bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Join the Climate Movement
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Every certification, every exchange, every retirement counts. Start making your impact today.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button onClick={() => navigate('/certify')} size="lg">
                Certify Products
              </Button>
              <Button onClick={() => navigate('/wallet')} variant="outline" size="lg">
                Go to Wallet
              </Button>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CarbonDashboardPage;
